import { trait } from 'koota'
import type { Entity, Trait } from 'koota'
import type Node from 'three/src/nodes/core/Node.js'
import type { Texture } from 'three'
import type { Sprite2D } from '../sprites/Sprite2D'
import type { ChannelName, ChannelNodeMap } from './channels'

// ============================================
// Schema Types
// ============================================

/** A single field value in an effect schema (type inferred from shape). */
export type EffectSchemaValue =
  | number
  | readonly [number, number]
  | readonly [number, number, number]
  | readonly [number, number, number, number]
  | (() => unknown)

/** An effect schema — maps field names to their default values. */
export type EffectSchema = Record<string, EffectSchemaValue>

/** Keys whose schema value is a plain number or tuple (→ TSL uniform, settable at runtime). */
export type UniformKeys<S extends EffectSchema> = {
  [K in keyof S]: S[K] extends (...args: never[]) => unknown ? never : K
}[keyof S]

/** Keys whose schema value is a factory function (→ typed constant, read-only reference). */
export type ConstantKeys<S extends EffectSchema> = {
  [K in keyof S]: S[K] extends (...args: never[]) => unknown ? K : never
}[keyof S]

/** Derive JS value types from an effect schema (uniform fields only, used by property setters). */
export type EffectValues<S extends EffectSchema> = {
  -readonly [K in UniformKeys<S>]: S[K] extends number
    ? number
    : S[K] extends readonly [number, number, number, number]
      ? [number, number, number, number]
      : S[K] extends readonly [number, number, number]
        ? [number, number, number]
        : S[K] extends readonly [number, number]
          ? [number, number]
          : never
}

/**
 * Read-only constants from factory function fields.
 * The reference is frozen at construction time and cannot be reassigned, but the
 * object's internals are freely mutable and mutations take effect immediately
 * (e.g. `this.forwardPlus.resize(w, h)` works live — no remove/re-add needed).
 */
export type EffectConstants<S extends EffectSchema> = {
  // Writable — backed by a getter/setter that reads/writes `_constants`.
  // Assignment is only meaningful before `sprite.addEffect(effect)` —
  // after attach, the effect's material routing is locked in by the
  // values in `_constants` at the time of attach.
  [K in ConstantKeys<S>]: S[K] extends () => infer R ? R : never
}

// ============================================
// Field Metadata
// ============================================

/** Computed field metadata from schema. */
export interface EffectField {
  /** Field name (unprefixed). */
  name: string
  /** Number of float components (1=float, 2=vec2, 3=vec3, 4=vec4). */
  size: number
  /** Default values as flat array. */
  default: number[]
}

// ============================================
// Node Context
// ============================================

/** Map a uniform schema value type to the corresponding parameterized Node type. */
export type SchemaToNodeType<V extends EffectSchemaValue> = V extends (...args: never[]) => unknown
  ? never
  : V extends number
    ? Node<'float'>
    : V extends readonly [number, number, number, number]
      ? Node<'vec4'>
      : V extends readonly [number, number, number]
        ? Node<'vec3'>
        : V extends readonly [number, number]
          ? Node<'vec2'>
          : Node<'float'> | Node<'vec2'> | Node<'vec3'> | Node<'vec4'>

/** Context passed to an effect's TSL node builder. */
export interface EffectNodeContext<S extends EffectSchema = EffectSchema> {
  /** The previous color in the effect chain (vec4 node). */
  inputColor: Node<'vec4'>
  /** Atlas UV coordinates (vec2 node). */
  inputUV: Node<'vec2'>
  /** TSL attribute nodes for each uniform schema field, keyed by unprefixed name. */
  attrs: { [K in UniformKeys<S>]: SchemaToNodeType<S[K]> }
  /** Read-only constants from factory function fields. */
  constants: EffectConstants<S>
}

/** Context passed to a provider effect's channel node builder. */
export interface ChannelNodeContext<S extends EffectSchema = EffectSchema> {
  /** Atlas UV coordinates (vec2 node). */
  atlasUV: Node<'vec2'>
  /** Read-only constants from factory function fields. */
  constants: EffectConstants<S>
  /** TSL attribute nodes for each uniform schema field, keyed by unprefixed name. */
  attrs: { [K in UniformKeys<S>]: SchemaToNodeType<S[K]> }
  /** Base sprite texture (for auto-normal generation, etc.). Null if unavailable. */
  baseTexture: Texture | null
}

// ============================================
// MaterialEffect Base Class
// ============================================

/**
 * Base class for per-sprite shader effects.
 *
 * Each MaterialEffect subclass defines:
 * - `effectName` — unique name for the effect
 * - `effectSchema` — per-sprite data schema with default values
 * - `buildNode()` — TSL node builder for the effect shader
 *
 * Each MaterialEffect instance:
 * - Has typed property accessors for each schema field
 * - Uses the snapshot pattern for pre-enrollment staging
 * - Dual-writes to ECS traits and packed GPU buffers
 *
 * @example Class-based definition:
 * ```typescript
 * class DissolveEffect extends MaterialEffect {
 *   static readonly effectName = 'dissolve'
 *   static readonly effectSchema = { progress: 0 } as const
 *   declare progress: number
 *
 *   static buildNode({ inputColor, attrs }: EffectNodeContext) {
 *     return mix(inputColor, vec4(0, 0, 0, 0), attrs.progress)
 *   }
 * }
 * ```
 *
 * @example Factory definition:
 * ```typescript
 * const DissolveEffect = createMaterialEffect({
 *   name: 'dissolve',
 *   schema: { progress: 0 },
 *   node({ inputColor, attrs }) {
 *     return mix(inputColor, vec4(0, 0, 0, 0), attrs.progress)
 *   },
 * })
 * ```
 */
export abstract class MaterialEffect {
  // ============================================
  // Static fields (shared across all instances of a subclass)
  // ============================================

  /** Unique effect name. Must be overridden by subclass. */
  static readonly effectName: string
  /** Per-sprite data schema with default values. Must be overridden by subclass. */
  static readonly effectSchema: EffectSchema
  /** Per-fragment channels this effect provides (e.g., ['normal']). */
  static readonly provides: readonly ChannelName[] = []
  /** Channel node builder — produces TSL nodes for declared channels. */
  static channelNode: ((channelName: string, context: ChannelNodeContext) => Node) | null = null

  /** @internal Auto-generated Koota trait from schema. */
  static _trait: Trait
  /** @internal Computed field metadata from schema. */
  static _fields: EffectField[]
  /** @internal Total float slots needed for this effect's data (excluding flags). */
  static _totalFloats: number
  /** @internal TSL node builder function. */
  static _node: (context: EffectNodeContext) => Node<'vec4'>
  /** @internal Whether static initialization has been performed. */
  static _initialized: boolean = false

  /**
   * TSL node builder. Must be overridden by subclass (class-based path).
   * The factory path sets this via static assignment.
   */
  static buildNode(_context: EffectNodeContext): Node<'vec4'> {
    throw new Error(`MaterialEffect.buildNode() not implemented for ${this.effectName}`)
  }

  /** @internal Factory functions for constant fields (keyed by field name). */
  static _constantFactories: Record<string, () => unknown>

  /**
   * Initialize static metadata from the schema (called once per subclass, lazily).
   * Computes field metadata, creates Koota trait, and sets up the node function.
   * @internal
   */
  static _initialize(): void {
    if (this._initialized) return
    this._initialized = true

    const schema = this.effectSchema
    if (!schema) {
      throw new Error(`MaterialEffect: ${this.name} is missing effectSchema`)
    }

    // Compute field metadata from schema defaults (uniform fields only)
    const fields: EffectField[] = []
    const constantFactories: Record<string, () => unknown> = {}
    let totalFloats = 0
    for (const [fieldName, value] of Object.entries(schema)) {
      if (typeof value === 'function') {
        // Constant field — factory function, not a uniform
        constantFactories[fieldName] = value as () => unknown
      } else if (typeof value === 'number') {
        fields.push({ name: fieldName, size: 1, default: [value] })
        totalFloats += 1
      } else {
        const arr = value as readonly number[]
        fields.push({ name: fieldName, size: arr.length, default: [...arr] })
        totalFloats += arr.length
      }
    }

    this._fields = fields
    this._totalFloats = totalFloats
    this._constantFactories = constantFactories

    // Build flattened trait schema for Koota (uniform fields only):
    // - float fields → { fieldName: default }
    // - vecN fields  → { fieldName_0: v[0], fieldName_1: v[1], ... }
    const traitSchema: Record<string, number> = {}
    for (const field of fields) {
      if (field.size === 1) {
        traitSchema[field.name] = field.default[0]!
      } else {
        for (let i = 0; i < field.size; i++) {
          traitSchema[`${field.name}_${i}`] = field.default[i]!
        }
      }
    }

    this._trait = trait(traitSchema)

    // Use buildNode as the node function
    this._node = this.buildNode.bind(this) as (context: EffectNodeContext) => Node<'vec4'>
  }

  // ============================================
  // Instance fields
  // ============================================

  /** Auto-incrementing unique ID for debugging. */
  static _nextId: number = 0

  /** Unique instance ID (like Three.js object ids). */
  readonly id: number

  /** Effect name (from static). */
  readonly name: string

  /** @internal The sprite this effect is attached to. */
  _sprite: Sprite2D | null = null

  /** @internal The ECS entity for the parent sprite. */
  _entity: Entity | null = null

  /** @internal Snapshot defaults for pre-enrollment staging. Keyed by field name. */
  _defaults: Record<string, number | number[]>

  /**
   * Per-instance constant values (from factory function schema fields).
   * References are frozen at construction time and cannot be reassigned.
   * Internal state is freely mutable and mutations apply immediately.
   * @internal
   */
  _constants: Record<string, unknown> = {}

  constructor() {
    const ctor = this.constructor as typeof MaterialEffect

    // Lazy initialize static metadata
    ctor._initialize()

    this.id = MaterialEffect._nextId++
    this.name = ctor.effectName

    // Build defaults snapshot from schema (uniform fields only)
    this._defaults = {}
    for (const field of ctor._fields) {
      if (field.size === 1) {
        this._defaults[field.name] = field.default[0]!
      } else {
        this._defaults[field.name] = [...field.default]
      }
    }

    // Set up property accessors for uniform schema fields
    for (const field of ctor._fields) {
      if (field.size === 1) {
        Object.defineProperty(this, field.name, {
          get: () => this._getField(field.name),
          set: (v: number) => this._setField(field.name, v),
          enumerable: true,
          configurable: true,
        })
      } else {
        Object.defineProperty(this, field.name, {
          get: () => this._getField(field.name),
          set: (v: number[]) => this._setField(field.name, v),
          enumerable: true,
          configurable: true,
        })
      }
    }

    // Initialize constant fields — call factory, store value, expose a
    // getter/setter that writes back into `_constants`. Assignment is
    // only meaningful BEFORE the effect is attached to a sprite/tilemap
    // (the effect's effectsKey / material routing is computed from
    // `_constants` at addEffect time). After attach, a set will update
    // the stored value but the effect's material binding is already
    // locked in — primarily useful for library code (and R3F default
    // prop-setting) that initializes a constant prior to calling
    // `sprite.addEffect(effect)`.
    for (const [name, factory] of Object.entries(ctor._constantFactories)) {
      const value = factory()
      this._constants[name] = value
      Object.defineProperty(this, name, {
        get: () => this._constants[name],
        set: (v: unknown) => {
          this._constants[name] = v
        },
        enumerable: true,
        configurable: true,
      })
    }
  }

  /**
   * Attach this effect to a sprite.
   * @internal Called by Sprite2D.addEffect()
   */
  _attach(sprite: Sprite2D): void {
    this._sprite = sprite
    this._entity = sprite._entity
  }

  /**
   * Detach this effect from its sprite.
   * @internal Called by Sprite2D.removeEffect()
   */
  _detach(): void {
    this._sprite = null
    this._entity = null
  }

  /**
   * Read a field value using the snapshot pattern.
   * If attached to an enrolled sprite, reads from ECS trait.
   * Otherwise, reads from the snapshot defaults.
   * @internal
   */
  _getField(name: string): number | number[] {
    const ctor = this.constructor as typeof MaterialEffect
    if (this._entity && this._entity.has(ctor._trait)) {
      // Read from trait
      const field = ctor._fields.find((f) => f.name === name)!
      const data = this._entity.get(ctor._trait) as Record<string, number>
      if (field.size === 1) {
        return data[name]!
      } else {
        const result: number[] = []
        for (let i = 0; i < field.size; i++) {
          result.push(data[`${name}_${i}`]!)
        }
        return result
      }
    }
    return this._defaults[name]!
  }

  /**
   * Write a field value using the snapshot pattern.
   * If attached to an enrolled sprite, writes to ECS trait (systems sync to GPU).
   * Otherwise, writes to the snapshot defaults.
   * Only triggers immediate GPU buffer sync for standalone sprites.
   * @internal
   */
  _setField(name: string, value: number | number[]): void {
    const ctor = this.constructor as typeof MaterialEffect

    if (this._entity && this._entity.has(ctor._trait)) {
      // Write to trait — systems will sync to batch buffers
      const field = ctor._fields.find((f) => f.name === name)!
      const data = this._entity.get(ctor._trait) as Record<string, number>

      if (field.size === 1) {
        // Skip if value unchanged — avoids unnecessary Changed triggers
        if (data[name] === value) return
        this._entity.set(ctor._trait, { [name]: value as number })
      } else {
        const arr = value as number[]
        const traitUpdate: Record<string, number> = {}
        let changed = false
        for (let i = 0; i < field.size; i++) {
          const key = `${name}_${i}`
          traitUpdate[key] = arr[i]!
          if (data[key] !== arr[i]) changed = true
        }
        if (!changed) return
        this._entity.set(ctor._trait, traitUpdate)
      }
    } else {
      // Write to snapshot defaults
      if (typeof value === 'number') {
        this._defaults[name] = value
      } else {
        this._defaults[name] = [...value]
      }
    }

    // Standalone only: immediate own-buffer write
    if (this._sprite && !this._sprite._entity) {
      this._sprite._writeEffectDataOwn()
    }
  }
}

// ============================================
// Factory: createMaterialEffect
// ============================================

/** Configuration passed to createMaterialEffect(). */
interface MaterialEffectConfig<S extends EffectSchema, C extends readonly ChannelName[] = readonly []> {
  /** Unique name for this effect. */
  name: string
  /** Per-sprite data schema — default values define types and initial values. */
  schema: S
  /** TSL node builder: receives input color, UV, and per-field attribute nodes. Optional for provider-only effects. */
  node?: (context: EffectNodeContext<S>) => Node<'vec4'>
  /**
   * Per-fragment channels this effect provides (e.g., `['normal'] as const`).
   * The `as const` (or literal array) is required for return-type narrowing
   * of {@link channelNode}.
   */
  provides?: C
  /**
   * Channel node builder — produces TSL nodes for declared channels.
   *
   * The return type is narrowed by the declared `provides`: if
   * `provides: ['normal'] as const`, the channelNode must return
   * `ChannelNodeMap['normal']` (i.e. `Node<'vec3'>`). For multi-channel
   * providers, the return is the union of `ChannelNodeMap[C[number]]` and
   * the function must narrow on `channelName` before returning.
   *
   * Omitting `provides` leaves `C = readonly []`, which makes this field
   * uncallable by type — a `channelNode` without a `provides` is a
   * compile-time error.
   */
  channelNode?: C extends readonly []
    ? never
    : (channelName: C[number], context: ChannelNodeContext<S>) => ChannelNodeMap[C[number]]
}

/**
 * Type for a MaterialEffect class created by the factory.
 * Instances have typed properties matching the schema.
 */
export type MaterialEffectClass<S extends EffectSchema> = {
  new (): MaterialEffect & EffectValues<S> & EffectConstants<S>
  readonly effectName: string
  readonly effectSchema: S
  readonly provides: readonly ChannelName[]
  readonly channelNode: ((channelName: string, context: ChannelNodeContext) => Node) | null
  readonly _trait: Trait
  readonly _fields: EffectField[]
  readonly _totalFloats: number
  readonly _constantFactories: Record<string, () => unknown>
  readonly _node: (context: EffectNodeContext) => Node<'vec4'>
  readonly _initialized: boolean
  _nextId: number
  _initialize(): void
  buildNode(context: EffectNodeContext<S>): Node<'vec4'>
}

/**
 * Create a MaterialEffect class from a configuration object.
 *
 * This is the simple factory path — for quick effect definitions without
 * writing a full class. Returns a class that extends MaterialEffect with
 * typed properties.
 *
 * @example
 * ```typescript
 * const DissolveEffect = createMaterialEffect({
 *   name: 'dissolve',
 *   schema: { progress: 0 },
 *   node({ inputColor, attrs }) {
 *     return mix(inputColor, vec4(0, 0, 0, 0), attrs.progress)
 *   },
 * })
 *
 * const dissolve = new DissolveEffect()
 * dissolve.progress = 0.5
 * sprite.addEffect(dissolve)
 * ```
 */
export function createMaterialEffect<
  const S extends EffectSchema,
  const C extends readonly ChannelName[] = readonly [],
>(config: MaterialEffectConfig<S, C>): MaterialEffectClass<S> {
  const { name, schema, node, provides: channelProvides, channelNode: channelNodeFn } = config

  // Create anonymous subclass with static fields
  const EffectClass = class extends MaterialEffect {
    static readonly effectName = name
    static readonly effectSchema = schema as EffectSchema
    static readonly provides: readonly ChannelName[] = channelProvides ?? []
    // Erase narrowed call-site types back to the storage surface (string, Node)
    // — the runtime dispatcher in buildMaterial() looks up by string channel
    // name and the pipeline resolves node types from ChannelNodeMap.
    static channelNode: ((ch: string, ctx: ChannelNodeContext) => Node) | null = channelNodeFn
      ? (ch: string, ctx: ChannelNodeContext) =>
          (channelNodeFn as unknown as (ch: string, ctx: ChannelNodeContext<S>) => Node)(
            ch,
            ctx as ChannelNodeContext<S>
          )
      : null
    static override _initialized: boolean = false

    static override buildNode(context: EffectNodeContext): Node<'vec4'> {
      if (node) return node(context as EffectNodeContext<S>)
      return context.inputColor
    }
  }

  // Give the class a readable name for debugging
  Object.defineProperty(EffectClass, 'name', { value: `${name}Effect` })

  return EffectClass as unknown as MaterialEffectClass<S>
}
